/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Box, LinearProgress, Typography, Button, InputLabel, MenuItem, Select, FormControl, SelectChangeEvent, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Link, List, ListItem, ListItemText, ListItemSecondaryAction, TextField, Chip, DialogContentText } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { 
    GridFilterModel, GridToolbarFilterButton, GridToolbarContainer, 
    GridToolbarColumnsButton, GridToolbarDensitySelector, 
    GridColDef, GridSortModel 
} from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Swal from 'sweetalert2';
// import { getRowsPerPageOptions } from '../components/AddEdit/CustomFunctions';
import { writeXlsxFile, formatUtcToLocal } from "../helper/utils";
import { apiClient } from '../helper/api';
import useAuth from '../context/AuthProvider';
import { PartnerType } from '../models/PartnersModel';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaDownload, FaRegEye, FaUpload } from 'react-icons/fa';
import { LightBlueBtnColor, DarkBlueBtnColor, HoverUnselectedLightBlueBtnColor, WhiteColor, GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../helper/theme';
import { AppRoutes } from '../helper/appRoutes';
import { DocumentViewer } from '../components/Elements/DocumentViewer';
import { UploadReportTagDocuments } from '../components/Elements/UploadReportTagDocuments';
import { UploadSignatureRequestDocuments } from '../components/Elements/UploadSignatureRequestDocuments';
import { ReportTagModel, ErrorTagCodeModel } from './ReportTag';
import JSZip from 'jszip';


export enum CategoryNames {
    TagNotification = 'Tag Notification',
    SignatureRequest = 'Signature Request',
    StampedDocuments = 'Stamped Documents',
    DiligentSearch = 'Diligent Search',
}

/** State-specific info for stamped documents: standard message MISC sends when emailing (SLA/DOIs requirements). */
const STAMPED_DOCUMENT_STATE_INFO: Record<string, { message: string; linkUrl: string }> = {
    Illinois: {
        message: 'Hello! Enclosed, please find Illinois SLA Countersignature page(s) for filings completed recently. For more information on the SLAI\'s requirements, please see here:',
        linkUrl: 'https://www.slai.org/docs/surplus-line-filing-taxes/reporting-policies-endorsements-other/',
    },
    'New York': {
        message: 'Hello! Enclosed, please find New York Stamped Declarations page(s) for filings completed recently. For more information on ELANY\'s requirements, please see here:',
        linkUrl: 'https://www.elany.org/publications-and-resources/publications/elany-says?url=dec-pages---binders---coverage-documents',
    },
};

function getStampedDocumentStateInfo(documentType: string | undefined): { message: string; linkUrl: string } | null {
    if (!documentType || typeof documentType !== 'string') return null;
    if (documentType.includes('Illinois')) return STAMPED_DOCUMENT_STATE_INFO.Illinois;
    if (documentType.includes('New York')) return STAMPED_DOCUMENT_STATE_INFO['New York'];
    return null;
}
export interface CustomerInboxModel {
    inboxId: number;
    partnersId: number;
    partnerName: string;
    userId: number;
    userName: string;

    reportTagId: number;
    stampedDocumentDetailId: number;
    signatureRequestId: number;
    diligentSearchLibraryId: number;
    message: string;
    clientInboxCategoryId: number;
    categoryName: string;

    isRead: boolean;
    isArchived: boolean;
    
    createdDate: string;
    createdBy: number;
    updatedDate: string;
    rowNum?: number;
    parentInboxId?: number | null;
    messageText?: string | null;
};
export interface ClientInboxResponse{
    clientInbox: CustomerInboxModel[];
    totalCount: number;
    totalPages: number;
    pageSize: number;
    pageNumber: number;
};
export interface CategoryModel{
    clientInboxCategoryId: number;
    categoryName: string;
};
const VenbrookPartnersId = 138;

export const CustomerInbox: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const token = user?.token;

    const [isPartnersFilter, setIsPartnersFilter] = useState<Boolean>(false);
    const [loading, setLoading] = useState(false);
    const [viewLoadingId, setViewLoadingId] = useState<number | null>(null);
    const [partners, setPartners] = useState<PartnerType[]>([{ partnersId: 0, customerName: '' }]);
    // const [isDiligentSearchUser, setIsDiligentSearchUser] = useState<boolean>(false);
    const [customerInbox, setCustomerInbox] = useState<CustomerInboxModel[]>([]);
    const [clientInboxCategories, setClientInboxCategories] = useState<CategoryModel[]>([]);

    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRows, setSelectedRows] = useState<CustomerInboxModel[]>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [],
    });
    // Default: newest first for both New Messages and Archive (Outlook-type expectation)
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdDate', sort: 'desc' }]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedClient, setSelectedClient] = useState<number | ''>('');

    const [selectedTab, setSelectedTab] = useState<'New Messages' | 'Archive'>('New Messages');

    const handleTabChange = (tab: 'New Messages' | 'Archive') => {
        if (tab !== selectedTab) {
            setSelectedTab(tab);
            setCurrentPage(1); // Reset to first page when tab changes
        }
    };

    const handleCategoryChange = (e: SelectChangeEvent) => {
        const newCategory = e.target.value;
        setSelectedCategory(newCategory);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleClientChange = (e: SelectChangeEvent) => {
        const value = e.target.value;
        const newClient = value === '' ? '' : Number(value);
        setSelectedClient(newClient);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handleClear = () => {
        setSelectedCategory('');
        setSelectedClient('');
        setCurrentPage(1); // Reset to first page when clearing filters
    };

    const markAsRead = () => {
        // only if all selected rows are unread
        if(selectedRows.length > 0) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => row.inboxId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to mark these messages as read?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to mark as read 
                    apiClient(`/clientInbox/markAsRead?clientInboxIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the customer inbox after flipping read status
                        fetchCustomerInbox();
                        setSelectedRows([]);
                        Swal.fire({
                            title: 'Messages marked as read',
                            text: 'The selected messages have been marked as read',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error updating read status:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No messages selected</div>',
                html: '<div style="color: white;">Please select at least one message to mark as read</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };

    /** Mark a single inbox item as read (e.g. when user opens a notification). No confirmation. */
    const markInboxItemAsRead = (inboxId: number) => {
        if (!inboxId) return;
        apiClient(`/clientInbox/markAsRead?clientInboxIdList=${inboxId}`, {
            method: 'POST',
            headers: new Headers({ Authorization: `Bearer ${token}` }),
        }).then(() => {
            fetchCustomerInbox();
        }).catch((err) => {
            console.error('Error marking inbox item as read:', err);
        });
    };

    const markAsUnread = () => {
        // only if all selected rows are read
        if(selectedRows.length > 0) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => row.inboxId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to mark these messages as unread?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to mark as unread
                    apiClient(`/clientInbox/markAsUnread?clientInboxIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the customer inbox after flipping read status
                        fetchCustomerInbox();
                        setSelectedRows([]);
                        Swal.fire({
                            title: 'Messages marked as unread',
                            text: 'The selected messages have been marked as unread',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error updating read status:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No messages selected</div>',
                html: '<div style="color: white;">Please select at least one message to mark as unread</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };
    const markAsArchived = () => {
        // only if all selected rows are unarchived
        if(selectedRows.length > 0 && selectedRows.every(row => row.isArchived === false)) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => row.inboxId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to move these messages to archive?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to move to archive
                    apiClient(`/clientInbox/markAsArchived?clientInboxIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the customer inbox after moving to archive
                        fetchCustomerInbox();
                        setSelectedRows([]);
                        Swal.fire({
                            title: 'Messages marked as archived',
                            text: 'The selected messages have been marked as archived',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error marking as archived:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No messages selected</div>',
                html: '<div style="color: white;">Please select at least one message to mark as archived</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };
    const markAsUnarchived = () => {
        // only if all selected rows are archived
        if(selectedRows.length > 0 && selectedRows.every(row => row.isArchived === true)) {
            setLoading(true);
            const selectedRowsIds = selectedRows.map(row => row.inboxId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to mark these messages as unarchived?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to mark as unarchived
                    apiClient(`/clientInbox/markAsUnarchived?clientInboxIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the customer inbox after marking as unarchived
                        fetchCustomerInbox();
                        Swal.fire({
                            title: 'Messages marked as unarchived',
                            text: 'The selected messages have been marked as unarchived',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error marking as unarchived:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No messages selected</div>',
                html: '<div style="color: white;">Please select at least one message to mark as unarchived</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
        setFilterModel(newFilterModel);
        setCurrentPage(1);  // Reset to first page when filter changes
    };
    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
        setCurrentPage(1);  // Reset to first page when sort changes
    };

    const handleCategoryClick = (row: CustomerInboxModel) => {
        switch (row.categoryName) {
            case CategoryNames.TagNotification:
                navigate(AppRoutes.home.reportTag);
                break;
            case CategoryNames.SignatureRequest:
                navigate(AppRoutes.home.requestSignature);
                break;
            case CategoryNames.StampedDocuments:
                navigate(AppRoutes.home.sendStampedDocument);
                break;
            case CategoryNames.DiligentSearch:
                navigate(AppRoutes.home.diligentSearchLibrary);
                break;
        };
    };

    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentsToView, setDocumentsToView] = useState<{documentUrl: string, documentName: string}[]>([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const handleCloseDocumentViewer = () => {
        setDocumentViewerOpen(false);
        setDocumentsToView([]);
        setCurrentDocumentIndex(0);
    };

    /** Stamped document info popup (MISC feedback: show state/SLA/DOIs requirements to client) */
    const [stampedDocumentInfoOpen, setStampedDocumentInfoOpen] = useState(false);
    const [stampedDocumentInfoContent, setStampedDocumentInfoContent] = useState<{ message: string; linkUrl: string } | null>(null);
    const [pendingStampedDocuments, setPendingStampedDocuments] = useState<{ documentUrl: string; documentName: string }[]>([]);
    const handleCloseStampedDocumentInfo = () => {
        setStampedDocumentInfoOpen(false);
        setStampedDocumentInfoContent(null);
        setPendingStampedDocuments([]);
    };
    const handleViewStampedDocumentsAfterInfo = () => {
        if (pendingStampedDocuments.length > 0) {
            setDocumentsToView(pendingStampedDocuments);
            setCurrentDocumentIndex(0);
            setDocumentViewerOpen(true);
        }
        handleCloseStampedDocumentInfo();
    };

    // Single Attached Documents dialog (shared for Report Tag, Signature Request, etc.)
    type AttachedDocumentsContext = { type: 'reportTag' | 'signatureRequest'; id: number } | null;
    const [attachedDocumentsOpen, setAttachedDocumentsOpen] = useState(false);
    const [attachedDocumentsList, setAttachedDocumentsList] = useState<{ id: string | number; documentName: string; documentUrl: string; createdDate?: string; sentBy?: string }[]>([]);
    const [attachedDocumentsContext, setAttachedDocumentsContext] = useState<AttachedDocumentsContext>(null);
    const handleCloseAttachedDocuments = () => {
        setAttachedDocumentsOpen(false);
        setAttachedDocumentsList([]);
        setAttachedDocumentsContext(null);
    };
    const viewDocumentFromList = (documentUrl: string, documentName: string) => {
        setDocumentsToView([{ documentUrl, documentName }]);
        setCurrentDocumentIndex(0);
        setDocumentViewerOpen(true);
    };
    const downloadSingleDocument = async (documentUrl: string, documentName: string) => {
        try {
            const response = await fetch(documentUrl, { method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-cache' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = documentName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            Swal.fire({ title: 'Error', text: error?.message || 'Failed to download document', icon: 'error', confirmButtonText: 'OK' });
        }
    };

    const [reportTag, setReportTag] = useState<ReportTagModel | null>(null);
    const [uploadReportTagDocumentsOpen, setUploadReportTagDocumentsOpen] = useState(false);
    const handleCloseUploadReportTagDocuments = () => {
        setUploadReportTagDocumentsOpen(false);
        setReportTag(null);
        setClientDocumentData({ reportTagId: 0, files: [] });
        fetchCustomerInbox();
    };
    const [uploadSignatureRequestOpen, setUploadSignatureRequestOpen] = useState(false);
    const [selectedSignatureRequestIdForUpload, setSelectedSignatureRequestIdForUpload] = useState<number>(0);
    const handleCloseUploadSignatureRequest = () => {
        setUploadSignatureRequestOpen(false);
        setSelectedSignatureRequestIdForUpload(0);
        fetchCustomerInbox();
    };

    /** Signature Request info popup (MISC details: licensee, state, form, due date, signature type, notarization) */
    type SignatureRequestInfoContent = {
        licensee: string;
        state: string;
        nameOfForm: string;
        dueDate: string;
        signatureType: string;
        notarizationRequired: string;
    };
    type SignatureRequestDialogState = {
        open: boolean;
        content: SignatureRequestInfoContent | null;
        signatureRequestId: number;
        documents: { id: string | number; documentName: string; documentUrl: string; createdDate?: string; sentBy?: string; createdByUserRole?: string }[];
    };
    const [signatureRequestDialog, setSignatureRequestDialog] = useState<SignatureRequestDialogState>({
        open: false,
        content: null,
        signatureRequestId: 0,
        documents: [],
    });
    const handleCloseSignatureRequestInfo = () => {
        setSignatureRequestDialog({ open: false, content: null, signatureRequestId: 0, documents: [] });
    };
    const handleViewSignatureRequestDocumentsFromInfo = () => {
        const docs = signatureRequestDialog.documents;
        if (docs.length === 0) return;
        if (docs.length === 1) {
            setDocumentsToView([{ documentUrl: docs[0].documentUrl, documentName: docs[0].documentName }]);
            setCurrentDocumentIndex(0);
            setDocumentViewerOpen(true);
        } else {
            setAttachedDocumentsList(docs);
            setAttachedDocumentsContext({ type: 'signatureRequest', id: signatureRequestDialog.signatureRequestId });
            setAttachedDocumentsOpen(true);
        }
        handleCloseSignatureRequestInfo();
    };
    const handleUploadSignatureRequestFromInfo = () => {
        setSelectedSignatureRequestIdForUpload(signatureRequestDialog.signatureRequestId);
        setUploadSignatureRequestOpen(true);
        handleCloseSignatureRequestInfo();
    };

    /** Tag Notification view popup (MESSAGE = tag summary + filer notes, ATTACHMENTS = report tag documents) */
    type TagNotificationDialogState = {
        open: boolean;
        reportTagId: number;
        inboxId: number;
        reportTag: ReportTagModel | null;
        errorTagCodes: ErrorTagCodeModel[];
        filerNotes: { filerNote: string; sentBy?: string; sentOn?: string; sentByUserRole?: string }[];
        documents: { id: string | number; documentName: string; documentUrl: string; createdDate?: string; sentBy?: string; createdByUserRole?: string }[];
    };
    const [tagNotificationDialog, setTagNotificationDialog] = useState<TagNotificationDialogState>({
        open: false,
        reportTagId: 0,
        inboxId: 0,
        reportTag: null,
        errorTagCodes: [],
        filerNotes: [],
        documents: [],
    });
    const [tagNotificationReplyToMisc, setTagNotificationReplyToMisc] = useState('');
    const handleCloseTagNotificationInfo = () => {
        setTagNotificationDialog({ open: false, reportTagId: 0, inboxId: 0, reportTag: null, errorTagCodes: [], filerNotes: [], documents: [] });
        setTagNotificationReplyToMisc('');
    };
    const handleReplyToMiscFromTag = () => {
        const replyToMisc = tagNotificationReplyToMisc?.trim();
        const inboxId = tagNotificationDialog.inboxId;
        if (!replyToMisc) return;
        setLoading(true);
        apiClient('/ReportTags/sendReplyToMisc', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify({ replyToMisc, inboxId }),
        }).then((response) => {
            if (response?.status !== false) {
                Swal.fire({ title: 'Reply sent', text: 'Your reply has been successfully sent to MISC.', icon: 'success', confirmButtonText: 'OK' });
                setTagNotificationReplyToMisc('');
                const reportTagId = tagNotificationDialog.reportTagId;
                apiClient(`/ReportTags/getFilerNotes?reportTagId=${reportTagId}`, {
                    method: 'GET',
                    headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
                }).then((res) => {
                    const data = res?.data ?? [];
                    const filerNotes = Array.isArray(data) ? data.map((item: { filerNote?: string; sentBy?: string; sentOn?: string; sentByUserRole?: string }) => ({ filerNote: item.filerNote ?? '', sentBy: item.sentBy, sentOn: item.sentOn, sentByUserRole: item.sentByUserRole })) : [];
                    setTagNotificationDialog((prev) => ({ ...prev, filerNotes }));
                }).catch(() => {});
            } else {
                throw new Error(response?.message || 'Failed to send reply');
            }
        }).catch((err) => {
            console.error('Error sending reply to MISC:', err);
            Swal.fire({ title: 'Error', text: err?.message || 'Failed to send reply to MISC', icon: 'error', confirmButtonText: 'OK' });
        }).finally(() => setLoading(false));
    };
    const handleUploadDocumentFromTagNotificationInfo = () => {
        setClientDocumentData({ reportTagId: tagNotificationDialog.reportTagId, files: [] });
        fetchReportTag(tagNotificationDialog.reportTagId);
        setUploadReportTagDocumentsOpen(true);
        handleCloseTagNotificationInfo();
    };

    // const [reportTags, setReportTags] = useState<ReportTagModel[]>([]);
    const [clientDocumentData, setClientDocumentData] = useState<{ reportTagId: number, files: File[]; inboxId?: number }>({ reportTagId: 0, files: [] });

    //#region View and Download functions for Report Tags, Signature Requests, & Stamped Documents
    // View and Download functions for Report Tags
    const viewReportTagDocuments = (reportTagId: number, inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((response) => {
            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this tag',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                return;
            }
            const documents = response.data.documents.map((doc: any, index: number) => ({
                id: doc.reportTagDocumentId ?? index,
                documentName: doc.documentName || 'Document',
                documentUrl: doc.documentUrl,
                createdDate: doc.dateCreated ?? doc.createdDate,
                sentBy: doc.createdByUsername ?? doc.createdBy ?? 'N/A',
                createdByUserRole: doc.createdByUserRole?? 'N/A',
            }));
            if (documents.length === 1) {
                setDocumentsToView([{ documentUrl: documents[0].documentUrl, documentName: documents[0].documentName }]);
                setCurrentDocumentIndex(0);
                setDocumentViewerOpen(true);
            } else {
                setAttachedDocumentsList(documents);
                setAttachedDocumentsContext({ type: 'reportTag', id: reportTagId });
                setAttachedDocumentsOpen(true);
            }
            if (documents.length === 1) {
                setDocumentsToView([{ documentUrl: documents[0].documentUrl, documentName: documents[0].documentName }]);
                setCurrentDocumentIndex(0);
                setDocumentViewerOpen(true);
            } else {
                setAttachedDocumentsList(documents);
                setAttachedDocumentsContext({ type: 'reportTag', id: reportTagId });
                setAttachedDocumentsOpen(true);
            }
        }).catch((error) => {
            console.error("Error fetching documents:", error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch documents',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }).finally(() => {
            setLoading(false);
            setViewLoadingId(null);
        });
    };

    /** Open Tag Notification popup (MESSAGE = tag summary + filer notes, ATTACHMENTS = documents) */
    const openTagNotificationView = (reportTagId: number, inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        const filerNotesPromise = apiClient(`/ReportTags/getFilerNotes?reportTagId=${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        }).then((res) => {
            const data = res?.data ?? [];
            return Array.isArray(data) ? data.map((item: { filerNote?: string; sentBy?: string; sentOn?: string; sentByUserRole?: string }) => ({
                filerNote: item.filerNote ?? '',
                sentBy: item.sentBy,
                sentOn: item.sentOn,
                sentByUserRole: item.sentByUserRole,
            })) : [];
        }).catch(() => []);
        const documentsPromise = apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        }).then((response) => {
            if (!response?.data?.documents?.length) return [];
            return response.data.documents.map((doc: any, index: number) => ({
                id: doc.reportTagDocumentId ?? index,
                documentName: doc.documentName || 'Document',
                documentUrl: doc.documentUrl ?? doc.blobAddress ?? '',
                createdDate: doc.dateCreated ?? doc.createdDate,
                sentBy: doc.createdByUsername ?? doc.sentBy ?? doc.createdBy ?? 'N/A',
                createdByUserRole: doc.createdByUserRole?? 'N/A',
            }));
        }).catch(() => []);
        const reportTagPromise = apiClient(`/ReportTags/getReportTagById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
        }).then((res) => res?.data ?? null).catch(() => null);
        const errorTagCodesPromise = apiClient('/ReportTags/getAllErrorTags', {
            method: 'GET',
            headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
        }).then((res) => Array.isArray(res?.data) ? res.data : []).catch(() => []);
        Promise.all([filerNotesPromise, documentsPromise, reportTagPromise, errorTagCodesPromise]).then(([filerNotes, documents, reportTag, errorTagCodes]) => {
            setTagNotificationDialog({ open: true, reportTagId, inboxId, reportTag, errorTagCodes, filerNotes, documents });
        }).finally(() => {
            setLoading(false);
            setViewLoadingId(null);
        });
    };

    const downloadReportTagDocuments = async (reportTagId: number) => {
        setLoading(true);
        try {
            const response = await apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            });

            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this tag',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const documents = response.data.documents;
            const folderName = `Report Tag #${reportTagId} - Documents`;

            Swal.fire({
                title: '<div style="color: white;">Download Documents</div>',
                html: `<div style="color: white;">This will download all documents to a zip file named <strong>${folderName}.zip</strong></div>`,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            }).then(async (confirmResult) => {
                if (!confirmResult.isConfirmed) {
                    setLoading(false);
                    return;
                }

                Swal.fire({
                    title: 'Preparing Download...',
                    html: 'Please wait while we prepare your files for download.',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const zip = new JSZip();
                let successCount = 0;
                let failCount = 0;
                const errors: string[] = [];
                const usedFileNames = new Set<string>();

                const generateUniqueFileName = (baseFileName: string): string => {
                    const fileExtension = baseFileName.includes('.') 
                        ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                        : '';
                    const fileNameWithoutExt = baseFileName.includes('.')
                        ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                        : baseFileName;
                    
                    let uniqueFileName = baseFileName;
                    let counter = 0;
                    while (usedFileNames.has(uniqueFileName)) {
                        counter++;
                        uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                    }
                    usedFileNames.add(uniqueFileName);
                    return uniqueFileName;
                };

                await Promise.all(
                    documents.map(async (doc: any) => {
                        try {
                            const response = await fetch(doc.documentUrl, {
                                method: 'GET',
                                mode: 'cors',
                                credentials: 'omit',
                                cache: 'no-cache',
                            });
                            
                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                            
                            const blob = await response.blob();
                            const uniqueFileName = generateUniqueFileName(doc.documentName || `document_${doc.reportTagDocumentId}`);
                            zip.file(uniqueFileName, blob);
                            successCount++;
                        } catch (error: any) {
                            failCount++;
                            const errorMsg = error.message || 'Failed to download';
                            errors.push(`${doc.documentName}: ${errorMsg}`);
                            console.error(`Error downloading ${doc.documentName}:`, error);
                        }
                    })
                );

                if (successCount === 0) {
                    throw new Error('Failed to download any documents');
                }

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const url = window.URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${folderName}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                Swal.close();
                
                if (failCount > 0) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Partial Success',
                        html: `
                            <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                            <p><strong>${failCount}</strong> document(s) failed to download.</p>
                            ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                        `,
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `All ${successCount} document(s) have been downloaded successfully.`,
                        confirmButtonText: 'OK',
                    });
                }
            }).catch((error) => {
                console.error('Error in download confirmation:', error);
            }).finally(() => {
                setLoading(false);
            });
        } catch (error: any) {
            console.error('Error in downloadReportTagDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
            setLoading(false);
        }
    };

    // View and Download functions for Signature Requests: show MISC details popup (Outlook-like message) then View/Upload
    const viewSignatureRequestDocuments = (signatureRequestId: number, inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        apiClient(`/SignatureRequests/getSignatureRequestById/${signatureRequestId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((response) => {
            if (!response) {
                Swal.fire({ title: 'Error', text: 'Failed to load signature request', icon: 'error', confirmButtonText: 'OK' });
                return;
            }
            const payload = response.data ?? response;
            const sr = Array.isArray(payload?.signatureRequest) ? payload.signatureRequest[0] : payload?.signatureRequest;
            const docsRaw = payload?.documents ?? (payload as any)?.Documents ?? response?.documents ?? (response as any)?.Documents ?? [];
            const docs = Array.isArray(docsRaw) ? docsRaw : [];
            const licensee = sr?.signerUserFirstName && sr?.signerUserLastName
                ? `${sr.signerUserFirstName} ${sr.signerUserLastName}`.trim()
                : (sr?.signerUserName ?? '—');
            const signatureTypes: string[] = [];
            if (sr?.isWetSignature) signatureTypes.push('Wet');
            if (sr?.isElectronicSignature) signatureTypes.push('Electronic');
            if (sr?.isDigitalSignature) signatureTypes.push('Digital');
            const signatureType = signatureTypes.length > 0 ? signatureTypes.join(', ') : '—';
            const notarizationRequired = sr?.notarizationRequired != null ? (sr.notarizationRequired ? 'Yes' : 'No') : '—';
            const mappedDocs = docs.map((doc: any, index: number) => ({
                id: String(doc.signatureRequestDocumentId ?? doc.blobAddress ?? `doc-${index}`),
                documentName: doc.documentName || 'Document',
                documentUrl: doc.blobAddress ?? doc.documentUrl ?? '',
                createdDate: doc.createdDate,
                sentBy: doc.createdByUsername ?? doc.createdBy ?? 'N/A',
                createdByUserRole: doc.createdByUserRole ?? '',
            }));
            setSignatureRequestDialog({
                open: true,
                content: {
                    licensee,
                    state: sr?.state ?? '—',
                    nameOfForm: sr?.formType ?? '—',
                    dueDate: sr?.dueDate ?? '—',
                    signatureType,
                    notarizationRequired,
                },
                signatureRequestId: signatureRequestId,
                documents: mappedDocs,
            });
        }).catch((error) => {
            console.error("Error fetching signature request:", error);
            Swal.fire({ title: 'Error', text: 'Failed to fetch signature request', icon: 'error', confirmButtonText: 'OK' });
        }).finally(() => {
            setLoading(false);
            setViewLoadingId(null);
        });
    };

    const downloadSignatureRequestDocuments = async (signatureRequestId: number) => {
        setLoading(true);
        try {
            const response = await apiClient(`/SignatureRequests/getSignatureRequestDocumentsById/${signatureRequestId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            });

            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this signature request',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const documents = response.data.documents;
            const folderName = `Signature Request #${signatureRequestId} - Documents`;

            Swal.fire({
                title: '<div style="color: white;">Download Documents</div>',
                html: `<div style="color: white;">This will download all documents to a zip file named <strong>${folderName}.zip</strong></div>`,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            }).then(async (confirmResult) => {
                if (!confirmResult.isConfirmed) {
                    setLoading(false);
                    return;
                }

                Swal.fire({
                    title: 'Preparing Download...',
                    html: 'Please wait while we prepare your files for download.',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const zip = new JSZip();
                let successCount = 0;
                let failCount = 0;
                const errors: string[] = [];
                const usedFileNames = new Set<string>();

                const generateUniqueFileName = (baseFileName: string): string => {
                    const fileExtension = baseFileName.includes('.') 
                        ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                        : '';
                    const fileNameWithoutExt = baseFileName.includes('.')
                        ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                        : baseFileName;
                    
                    let uniqueFileName = baseFileName;
                    let counter = 0;
                    while (usedFileNames.has(uniqueFileName)) {
                        counter++;
                        uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                    }
                    usedFileNames.add(uniqueFileName);
                    return uniqueFileName;
                };

                await Promise.all(
                    documents.map(async (doc: any) => {
                        try {
                            const response = await fetch(doc.documentUrl, {
                                method: 'GET',
                                mode: 'cors',
                                credentials: 'omit',
                                cache: 'no-cache',
                            });
                            
                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                            
                            const blob = await response.blob();
                            const uniqueFileName = generateUniqueFileName(doc.documentName || `document_${doc.signatureRequestDocumentId}`);
                            zip.file(uniqueFileName, blob);
                            successCount++;
                        } catch (error: any) {
                            failCount++;
                            const errorMsg = error.message || 'Failed to download';
                            errors.push(`${doc.documentName}: ${errorMsg}`);
                            console.error(`Error downloading ${doc.documentName}:`, error);
                        }
                    })
                );

                if (successCount === 0) {
                    throw new Error('Failed to download any documents');
                }

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const url = window.URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${folderName}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                Swal.close();
                
                if (failCount > 0) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Partial Success',
                        html: `
                            <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                            <p><strong>${failCount}</strong> document(s) failed to download.</p>
                            ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                        `,
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `All ${successCount} document(s) have been downloaded successfully.`,
                        confirmButtonText: 'OK',
                    });
                }
            }).catch((error) => {
                console.error('Error in download confirmation:', error);
            }).finally(() => {
                setLoading(false);
            });
        } catch (error: any) {
            console.error('Error in downloadSignatureRequestDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
            setLoading(false);
        }
    };

    // View and Download functions for Stamped Documents
    const viewStampedDocuments = (stampedDocumentDetailId: number, inboxId: number) => {
        markInboxItemAsRead(inboxId);
        setViewLoadingId(inboxId);
        setLoading(true);
        apiClient(`/StampedDocument/getStampedDocumentById/${stampedDocumentDetailId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((response) => {
            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this stamped document',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                return;
            }
            const documents = response.data.documents.map((doc: any) => ({
                documentUrl: doc.blobAddress,
                documentName: doc.documentName || 'Document'
            }));
            const documentType = response.data?.stampedDocumentDetail?.documentType;
            const stateInfo = getStampedDocumentStateInfo(documentType);
            // Show state/SLA info popup when MISC indicated Illinois or New York, so client sees what was sent and requirements
            if (stateInfo) {
                setStampedDocumentInfoContent(stateInfo);
                setPendingStampedDocuments(documents);
                setStampedDocumentInfoOpen(true);
            } else {
                setDocumentsToView(documents);
                setCurrentDocumentIndex(0);
                setDocumentViewerOpen(true);
            }
        }).catch((error) => {
            console.error("Error fetching documents:", error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch documents',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }).finally(() => {
            setLoading(false);
            setViewLoadingId(null);
        });
    };

    const downloadStampedDocuments = async (stampedDocumentDetailId: number) => {
        setLoading(true);
        try {
            const response = await apiClient(`/StampedDocument/getStampedDocumentById/${stampedDocumentDetailId}`, {
                method: 'GET',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            });

            if (!response || !response.data || !response.data.documents || response.data.documents.length === 0) {
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this stamped document',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                return;
            }

            const documents = response.data.documents;
            const folderName = `Stamped Document #${stampedDocumentDetailId} - Documents`;

            Swal.fire({
                title: '<div style="color: white;">Download Documents</div>',
                html: `<div style="color: white;">This will download all documents to a zip file named <strong>${folderName}.zip</strong></div>`,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            }).then(async (confirmResult) => {
                if (!confirmResult.isConfirmed) {
                    setLoading(false);
                    return;
                }

                Swal.fire({
                    title: 'Preparing Download...',
                    html: 'Please wait while we prepare your files for download.',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                const zip = new JSZip();
                let successCount = 0;
                let failCount = 0;
                const errors: string[] = [];
                const usedFileNames = new Set<string>();

                const generateUniqueFileName = (baseFileName: string): string => {
                    const fileExtension = baseFileName.includes('.') 
                        ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                        : '';
                    const fileNameWithoutExt = baseFileName.includes('.')
                        ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                        : baseFileName;
                    
                    let uniqueFileName = baseFileName;
                    let counter = 0;
                    while (usedFileNames.has(uniqueFileName)) {
                        counter++;
                        uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                    }
                    usedFileNames.add(uniqueFileName);
                    return uniqueFileName;
                };

                await Promise.all(
                    documents.map(async (doc: any) => {
                        try {
                            const response = await fetch(doc.blobAddress, {
                                method: 'GET',
                                mode: 'cors',
                                credentials: 'omit',
                                cache: 'no-cache',
                            });
                            
                            if (!response.ok) {
                                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                            }
                            
                            const blob = await response.blob();
                            const uniqueFileName = generateUniqueFileName(doc.documentName || `document_${doc.stampedDocumentId}`);
                            zip.file(uniqueFileName, blob);
                            successCount++;
                        } catch (error: any) {
                            failCount++;
                            const errorMsg = error.message || 'Failed to download';
                            errors.push(`${doc.documentName}: ${errorMsg}`);
                            console.error(`Error downloading ${doc.documentName}:`, error);
                        }
                    })
                );

                if (successCount === 0) {
                    throw new Error('Failed to download any documents');
                }

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const url = window.URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${folderName}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                Swal.close();
                
                if (failCount > 0) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Partial Success',
                        html: `
                            <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                            <p><strong>${failCount}</strong> document(s) failed to download.</p>
                            ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                        `,
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `All ${successCount} document(s) have been downloaded successfully.`,
                        confirmButtonText: 'OK',
                    });
                }
            }).catch((error) => {
                console.error('Error in download confirmation:', error);
            }).finally(() => {
                setLoading(false);
            });
        } catch (error: any) {
            console.error('Error in downloadStampedDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
            setLoading(false);
        }
    };
    //#endregion

    const columns: GridColDef[] = [
        { field: 'message', headerName: 'Message', flex: 5,
            renderCell: (params) => {
                const category = params.row.categoryName;
                let exclamationCount = 0;
                
                if (category === CategoryNames.TagNotification) {
                    exclamationCount = 1;
                } else if (category === CategoryNames.SignatureRequest) {
                    exclamationCount = 2;
                }
                
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {exclamationCount > 0 && (
                            <Box sx={{ color: 'red', fontWeight: 'bold' }}>
                                {'!'.repeat(exclamationCount)}
                            </Box>
                        )}
                        {params.row.isRead ? <span>{params.value}</span> : <span><strong>{params.value}</strong></span>}
                    </Box>
                );
            }
        },
        { field: 'categoryName', headerName: 'Category', flex: 1.5, renderCell: (params) => {
            const category = params.row.categoryName;
            const isRead = params.row.isRead;
            return isRead ? category : <strong>{category}</strong>;
        } },
        { field: 'partnerName', headerName: 'Client', flex: 2, renderCell: (params) => {
            const client = params.row.partnerName;
            const isRead = params.row.isRead;
            return isRead ? <span>{client}</span> : <span><strong>{client}</strong></span>;
        } },
        { field: 'createdDate', headerName: 'Date', flex: 1.5, 
            // align: 'right' 
            renderCell: (params) => {
                const createdDate = params.row.createdDate ? formatUtcToLocal(params.row.createdDate, 'MM-DD-YYYY hh:mm A') : '';
                const isRead = params.row.isRead;
                return isRead ? <span>{createdDate}</span> : <span><strong>{createdDate}</strong></span>;
            }
        },
        { field: 'action', headerName: 'Action', flex: 1.5, 
            align: 'center', headerAlign: 'center',
            renderCell: (params) => {
                const isInboxItemLoading = Boolean(viewLoadingId === params.row.inboxId);
                const ViewDocumentsIcon = () => <FaRegEye />;
                const handleUploadDocuments = (e: any) => {
                    e?.stopPropagation?.();
                    e?.stopPropagation?.();
                    switch(params.row.categoryName) {
                        case CategoryNames.TagNotification:
                            setClientDocumentData({ reportTagId: params.row.reportTagId, files: [], inboxId: params.row.inboxId });
                            fetchReportTag(params.row.reportTagId);
                            setUploadReportTagDocumentsOpen(true);
                            break;
                        case CategoryNames.SignatureRequest:
                            if (params.row.signatureRequestId) {
                                setSelectedSignatureRequestIdForUpload(params.row.signatureRequestId);
                                setUploadSignatureRequestOpen(true);
                            }
                            if (params.row.signatureRequestId) {
                                setSelectedSignatureRequestIdForUpload(params.row.signatureRequestId);
                                setUploadSignatureRequestOpen(true);
                            }
                            break;
                        case CategoryNames.StampedDocuments:
                            break;
                        case CategoryNames.DiligentSearch:
                            break;
                        default:
                            break;
                    };
                };
                const handleViewDocuments = (e: any) => {
                    switch(params.row.categoryName) {
                        case CategoryNames.TagNotification:
                            e.stopPropagation();
                            if (params.row.reportTagId) {
                                openTagNotificationView(params.row.reportTagId, params.row.inboxId);
                            }
                            break;
                        case CategoryNames.SignatureRequest:
                            e.stopPropagation();
                            if (params.row.signatureRequestId) {
                                viewSignatureRequestDocuments(params.row.signatureRequestId, params.row.inboxId);
                            }
                            break;
                        case CategoryNames.StampedDocuments:
                            e.stopPropagation();
                            if (params.row.stampedDocumentDetailId) {
                                viewStampedDocuments(params.row.stampedDocumentDetailId, params.row.inboxId);
                            }
                            break;
                        case CategoryNames.DiligentSearch:
                            break;
                        default:
                            break;
                    };
                };
                const handleDownloadDocuments = (e: any) => {
                    switch(params.row.categoryName) {
                        case CategoryNames.TagNotification:
                            e.stopPropagation();
                            if (params.row.reportTagId) {
                                downloadReportTagDocuments(params.row.reportTagId);
                            }
                            break;
                        case CategoryNames.SignatureRequest:
                            e.stopPropagation();
                            if (params.row.signatureRequestId) {
                                downloadSignatureRequestDocuments(params.row.signatureRequestId);
                            }
                            break;
                        case CategoryNames.StampedDocuments:
                            e.stopPropagation();
                            if (params.row.stampedDocumentDetailId) {
                                downloadStampedDocuments(params.row.stampedDocumentDetailId);
                            }
                            break;
                        case CategoryNames.DiligentSearch:
                            break;
                        default:
                            break;
                    };
                };
                const isTagNotification = params.row.categoryName === CategoryNames.TagNotification;
                const isSignatureRequest = params.row.categoryName === CategoryNames.SignatureRequest;
                const isStampedDocuments = params.row.categoryName === CategoryNames.StampedDocuments;
                const isDiligentSearch = params.row.categoryName === CategoryNames.DiligentSearch;
                const slotSx = { width: 36, display: 'flex', justifyContent: 'center' };

                const viewTooltipTitle = isSignatureRequest
                    ? 'View & Upload Documents Attached'
                    : 'View Documents Attached';
                const downloadTooltipTitle = isSignatureRequest
                    ? 'Download Documents'
                    : 'Download all Documents Attached';

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.0 }}>
                        <Box sx={slotSx}>
                            {(isTagNotification || isSignatureRequest) && (
                                <Tooltip title="Upload Documents" arrow={true}>
                                    <IconButton sx={{ color: LightBlueBtnColor }}
                                        onClick={handleUploadDocuments}
                                    ><FaUpload /></IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        <Box sx={slotSx}>
                            {(isTagNotification || isSignatureRequest || isStampedDocuments) && (
                                <Tooltip title={viewTooltipTitle} arrow={true}>
                                    <IconButton sx={{ color: LightBlueBtnColor }}
                                        disabled={isInboxItemLoading}
                                        onClick={handleViewDocuments}
                                    >
                                        <ViewDocumentsIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        <Box sx={slotSx}>
                            {(isTagNotification || isSignatureRequest || isStampedDocuments) && (
                                <Tooltip title={downloadTooltipTitle} arrow={true}>
                                    <IconButton sx={{ color: LightBlueBtnColor }}
                                        onClick={handleDownloadDocuments}
                                    ><FaDownload /></IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        <Box sx={slotSx}>
                            {isDiligentSearch && (
                                <Tooltip title={`Go to ${params.row.categoryName}`} arrow={true}>
                                    <IconButton onClick={() => {handleCategoryClick(params.row);}}
                                        sx={{ color: LightBlueBtnColor }}
                                    >
                                        <FaArrowRight />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                );
            }
        }
    ];


    useEffect(() => {
        fetchUserPartners();
        // getIsDiligentSearchUser();
        fetchClientInboxCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchCustomerInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, filterModel, sortModel, selectedTab, selectedCategory, selectedClient]);

    const fetchUserPartners = () => {
        setLoading(true);
        // const partnersUrl = '/partners/getPartners';
        const partnersUrl = '/customer/getPartnersList';
        apiClient(partnersUrl, {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then((responseJson) => {
            if (!responseJson) return;
            setPartners(responseJson.data as PartnerType[]);
            // check if the user is a venbrook customer
            setIsPartnersFilter(Boolean(responseJson.data.some(
                (partner: PartnerType) => partner.partnersId === VenbrookPartnersId
            )));
        }).catch(error => {
            console.error('Error fetching partners:', error);
        }).finally(() => {
            setLoading(false);
        });
    };
    const fetchClientInboxCategories = () => {
        setLoading(true);
        apiClient('/clientInbox/getInboxCategories', {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then((responseJson) => {
            if (!responseJson) return;
            setClientInboxCategories(responseJson.data as CategoryModel[]);
        }).catch(error => {
            console.error("Error fetching client inbox categories:", error);
        }).finally(() => {
            setLoading(false);
        });
    };
    const fetchCustomerInbox = () => {
        setLoading(true);
        
        // Convert filterModel to the format expected by the API
        const filterModelJson = filterModel && filterModel.items && filterModel.items.length > 0 ? filterModel : null;
        
        // Get sort field and direction from sortModel
        const sortField = sortModel && sortModel.length > 0 ? sortModel[0].field || '' : '';
        const sortDirection = sortModel && sortModel.length > 0 ? sortModel[0].sort || '' : '';
        
        // Determine isArchived based on selectedTab
        const isArchived = selectedTab === 'Archive' ? true : false;
        
        apiClient(`/clientInbox/getAllClientInbox`, {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
            body: JSON.stringify({
                pageSize: pageSize,
                pageNumber: currentPage,
                field: sortField,
                sort: sortDirection,
                filterModel: filterModelJson,
                isArchived: isArchived,
                clientInboxCategoryId: selectedCategory ? parseInt(selectedCategory) : 0,
                partnersId: typeof selectedClient === 'number' ? selectedClient : 0,
            }),
        }).then((responseJson) => {
            if (!responseJson) return;
            const { clientInbox, totalCount, totalPages, pageSize, pageNumber } = responseJson.data as ClientInboxResponse;
            
            // No need to sort or filter client-side - server handles it
            setCustomerInbox(clientInbox);
            setTotalCount(totalCount);
        }).catch((error) => {
            console.error('Error fetching customer inbox:', error);
            setCustomerInbox([]);
            setTotalCount(0);
        }).finally(() => {
            setLoading(false);
        });
    };

    const exportInboxToExcel = () => {
        const buildExportRows = (rows: CustomerInboxModel[]): (string | number)[][] => {
            const headers = ['Message', 'Category', 'Client', 'Date', 'Read', 'Archived'];
            const dataRows = rows.map((row) => [
                row.message ?? '',
                row.categoryName ?? '',
                row.partnerName ?? '',
                row.createdDate ? formatUtcToLocal(row.createdDate, 'MM/DD/YYYY hh:mm A') : '',
                row.isRead ? 'Yes' : 'No',
                row.isArchived ? 'Yes' : 'No',
            ]);
            return [headers, ...dataRows];
        };

        const doExport = (data: CustomerInboxModel[]) => {
            if (!data || data.length === 0) {
                Swal.fire({ title: 'No data', text: 'No inbox messages to export.', icon: 'info', confirmButtonText: 'OK' });
                return;
            }
            const exportData = buildExportRows(data);
            const filename = `ClientInbox_${formatUtcToLocal(new Date().toISOString(), 'YYYY-MM-DD_HHmm')}.xlsx`;
            writeXlsxFile(exportData, filename);
        };

        if (selectedRows.length > 0) {
            doExport(selectedRows);
            return;
        }

        setLoading(true);
        const filterModelJson = filterModel?.items?.length ? filterModel : null;
        const sortField = sortModel?.[0]?.field ?? '';
        const sortDirection = sortModel?.[0]?.sort ?? '';
        const isArchived = selectedTab === 'Archive';
        const exportPageSize = Math.max(totalCount || 1, 1);
        apiClient(`/clientInbox/getAllClientInbox`, {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
            body: JSON.stringify({
                pageSize: exportPageSize,
                pageNumber: 1,
                field: sortField,
                sort: sortDirection,
                filterModel: filterModelJson,
                isArchived,
                clientInboxCategoryId: selectedCategory ? parseInt(selectedCategory, 10) : 0,
                partnersId: typeof selectedClient === 'number' ? selectedClient : 0,
            }),
        }).then((responseJson) => {
            if (!responseJson?.data?.clientInbox) {
                doExport(customerInbox);
                return;
            }
            doExport(responseJson.data.clientInbox as CustomerInboxModel[]);
        }).catch(() => {
            doExport(customerInbox);
        }).finally(() => {
            setLoading(false);
        });
    };

    const fetchReportTag = (reportTagId: number) => {
        setLoading(true);
        apiClient(`/ReportTags/getReportTagById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setReportTag(responseJson.data);
        }).catch(error => {
            console.error("Error fetching report tag:", error)
        }).finally(() => {
            setLoading(false);
        });
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarFilterButton className="me-3 sky-blue" />
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" defaultValue={'compact'} />
            </GridToolbarContainer>
        );
    };
    function EditToolbar() {
        return (
            <div className=" p-2 d-flex justify-content-end datagrid-butons">
                <div className="ml-2"><CustomToolbar /></div>
            </div>
        );
    };

    const ClientDropdown = () => (
        <FormControl size="small" className="select-input-border text-input-box" sx={{ width: 220 }}>
            <InputLabel id="client-label" sx={{ top: '5px' }}>Client</InputLabel>
            <Select label="Client" labelId="client-label" id="client"
                value={selectedClient === '' ? '' : selectedClient.toString()}
                onChange={handleClientChange}
            >
                <MenuItem value=""><em>Client</em></MenuItem>
                {partners.map((partner, index) => (
                    <MenuItem key={index} value={partner.partnersId}>
                        {partner.customerName}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
    
    var headerHeight = document.getElementsByClassName('customer-header').length > 0 ? document.getElementsByClassName('customer-header')[0].clientHeight : 0;
    headerHeight = document.getElementsByClassName('img-bg-sec').length ? headerHeight + document.getElementsByClassName('img-bg-sec')[0].clientHeight : headerHeight;
    headerHeight = document.getElementsByClassName('MuiDataGrid-footerContainer').length > 0 ? headerHeight + document.getElementsByClassName('MuiDataGrid-footerContainer')[0].clientHeight : headerHeight;
    
    const updateInboxItemButtonStyling = {
        borderRadius: '25px',
        padding: '10px 20px',
        textTransform: 'none',
        fontSize: '16px',
        fontWeight: 500,
        backgroundColor:  LightBlueBtnColor,
        color: WhiteColor,
        '&:hover': {
            backgroundColor: DarkBlueBtnColor,
        },
    };
    
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ width: '100%' }}>
            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                input::placeholder {
                    color: #333;
                    opacity: 1;
                }
                `}
            </style>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: "99999", width: "100%" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>
            
            <Box sx={{ width: '100%', }} className="customer-header">
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '20px'
                }}>
                    {/* Left side with Welcome message */}
                    <Box>
                        <Typography variant="h4" 
                            component="h1" 
                            sx={{ 
                                fontSize: '28px',
                                fontWeight: 'normal',
                                marginBottom: '8px'
                            }}
                        >
                            <strong>Inbox</strong>
                        </Typography>
                        <Typography variant="body1" 
                            sx={{ 
                                fontSize: '16px',
                                color: '#555'
                            }}
                        >Select a category to view more details.</Typography>
                    </Box>
                </Box>
            </Box>
            
            {/* Tabs Section */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: '20px' }}>
                <div className="text-center">
                    <Button onClick={() => handleTabChange('New Messages')}
                        variant={selectedTab === 'New Messages' ? 'contained' : 'outlined'}
                        sx={{
                            borderRadius: '25px',
                            padding: '10px 40px',
                            textTransform: 'none',
                            fontSize: '16px',
                            fontWeight: 500,
                            backgroundColor: selectedTab === 'New Messages' ? LightBlueBtnColor : 'transparent',
                            color: selectedTab === 'New Messages' ? WhiteColor : LightBlueBtnColor,
                            borderColor: LightBlueBtnColor,
                            '&:hover': {
                                backgroundColor: selectedTab === 'New Messages' ? DarkBlueBtnColor : HoverUnselectedLightBlueBtnColor,
                                borderColor: LightBlueBtnColor,
                            },
                        }}
                    >New Messages</Button>
                </div>
                <div className="text-center">
                    <Button onClick={() => handleTabChange('Archive')}
                        variant={selectedTab === 'Archive' ? 'contained' : 'outlined'}
                        sx={{
                            borderRadius: '25px',
                            padding: '10px 40px',
                            textTransform: 'none',
                            fontSize: '16px',
                            fontWeight: 500,
                            backgroundColor: selectedTab === 'Archive' ? LightBlueBtnColor : 'transparent',
                            color: selectedTab === 'Archive' ? WhiteColor : LightBlueBtnColor,
                            borderColor: LightBlueBtnColor,
                            '&:hover': {
                                backgroundColor: selectedTab === 'Archive' ? DarkBlueBtnColor : HoverUnselectedLightBlueBtnColor,
                                borderColor: LightBlueBtnColor,
                            },
                        }}
                    >Archive</Button>
                </div>
            </Box>
            
            {/* Filter Section */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                ml: '100px', // Adds margin-left
            }}>
                {/* Mark as Read Button */}
                <Button variant={ 'contained' }
                    onClick={markAsRead}
                    sx={updateInboxItemButtonStyling}
                >Mark as Read</Button>
                
                {/* Mark as Unread Button */}
                <Button variant={ 'contained' }
                    onClick={markAsUnread}
                    sx={updateInboxItemButtonStyling}
                >Mark as Unread</Button>
                
                {/* Move to Archive Button */}
                <Button variant={ 'contained' }
                    onClick={selectedTab === 'New Messages' ? markAsArchived : markAsUnarchived}
                    sx={updateInboxItemButtonStyling}
                >{selectedTab === 'New Messages' ? 'Move to Archive' : 'Move to New Messages'}</Button>
                
                
                <Box sx={{ marginLeft: '60px' }}>
                    <Typography variant="h4" component="h1" 
                        sx={{ 
                            fontSize: '16px',
                            fontWeight: 'normal',
                            // marginBottom: '8px'
                        }}
                    >Filter by:</Typography>
                </Box>
                
                {/* Category Dropdown */}
                <FormControl size="small" className="select-input-border text-input-box" sx={{ width: 220 }}>
                    <InputLabel id="category-label" sx={{ top: '5px' }}>Category</InputLabel>
                    <Select label="Category" labelId="category-label" id="category"
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                    >
                        <MenuItem value=""><em>Category</em></MenuItem>
                        {clientInboxCategories.map((category) => (
                            <MenuItem key={category.clientInboxCategoryId} value={category.clientInboxCategoryId}>
                                {category.categoryName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                {/* client dropdown */}
                {isPartnersFilter && <ClientDropdown />}
                
                <Button variant={ 'outlined' } onClick={handleClear}
                    sx={{
                        borderRadius: '25px',
                        padding: '10px 20px',
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 500,
                        backgroundColor:  WhiteColor,
                        color: '#000',
                        borderColor: LightBlueBtnColor,
                        '&:hover': {
                            backgroundColor: HoverUnselectedLightBlueBtnColor,
                            color: '#000',
                            borderColor: LightBlueBtnColor
                        },
                    }}
                >Clear</Button>
                <Button variant="outlined" onClick={exportInboxToExcel} disabled={loading}
                    sx={{
                        borderRadius: '25px',
                        padding: '10px 20px',
                        textTransform: 'none',
                        fontSize: '16px',
                        fontWeight: 500,
                        backgroundColor: WhiteColor,
                        color: '#000',
                        borderColor: LightBlueBtnColor,
                        marginLeft: '8px',
                        '&:hover': {
                            backgroundColor: HoverUnselectedLightBlueBtnColor,
                            color: '#000',
                            borderColor: LightBlueBtnColor
                        },
                    }}
                >Export </Button>
            </Box>
            
            <Box className="shadow rounded" 
                sx={{ 
                    height: 'calc(100vh - '+(headerHeight + 70)+'px)', 
                    width: '100%', 
                    backgroundColor: WhiteColor, 
                    padding: '0 !important', 
                    overflow: 'auto',
                    border: '1px solid #8cc5e6' 
                }}
            >
                <DataGridPro rows={customerInbox}
                    columns={columns}
                    getRowId={(r) => r.inboxId}
                    classes={{
                        columnHeaders: "MuiDataGridPro-columnHeaders custom-header",
                        virtualScroller: "MuiDataGridPro-virtualScroller",
                    }}
                    rowCount={totalCount}
                    page={currentPage - 1}
                    pageSize={pageSize}
                    pagination
                    paginationMode="server"
                    onPageChange={(newPage: number) => setCurrentPage(newPage + 1)}
                    rowsPerPageOptions={[25, 50, 100, 250, 500, 1000]}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    filterMode="server"
                    filterModel={filterModel}
                    onFilterModelChange={handleFilterModelChange}
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    disableSelectionOnClick
                    loading={loading && viewLoadingId === null}

                    checkboxSelection={true}
                    selectionModel={selectedRows.map(row => row.inboxId).filter((id): id is number => id !== undefined)}
                    onSelectionModelChange={(newSelectionModel) => {
                        // Map the selected IDs to full CustomerInboxModel objects
                        const selectedObjects = (newSelectionModel as number[])
                            .map(id => customerInbox.find(row => row?.inboxId === id))
                            .filter((row): row is CustomerInboxModel => row !== undefined);
                        setSelectedRows(selectedObjects);
                    }}
                    
                    components={{
                        Toolbar: EditToolbar,
                        // message to show when no data is found
                        NoRowsOverlay: () => (
                            <Typography variant="body1" sx={{ textAlign: 'center', padding: '20px' }}>No inbox messages found</Typography>
                        ),
                    }}
                    
                    sx={{
                        "& .MuiDataGrid-columnHeaderTitle": {
                            whiteSpace: "normal",
                            lineHeight: "normal",
                        },
                        // "&.MuiDataGrid-root--densityCompact": {
                        //     lineHeight: "1",
                        // },
                        // '&.MuiDataGrid-root--densityStandard': {
                        //     lineHeight: "3",
                        // },
                        // '&.MuiDataGrid-root--densityComfortable': {
                        //     lineHeight: "5",
                        // },
                        "& .MuiDataGrid-virtualScroller": {
                            marginTop: "50px !important",
                        },
                        "& .MuiDataGrid-cell[data-field='dateCreated']": {
                            paddingRight: "36px",
                        },
                        "& .MuiDataGrid-columnHeader[data-field='dateCreated']": {
                            paddingRight: "36px",
                        },
                        // "& .MuiDataGrid-columnHeaderCheckbox": {
                        //     display: "none",
                        // },
                        "& .MuiDataGrid-cell": {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                        },
                        "& .MuiDataGrid-columnHeader": {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                        },
                        fontFamily: "'Proxima Nova', sans-serif",
                        border: "0px",
                        fontSize: "16px",
                        minWidth: "fit-content",
                    }}
                />
            </Box>

            {/* Signature Request – Message Center layout (no Reply, no Send & Return); Upload Document in ATTACHMENTS */}
            <Dialog
                open={signatureRequestDialog.open}
                onClose={handleCloseSignatureRequestInfo}
                fullWidth
                maxWidth="xl"
                PaperProps={{ style: { width: '98%', maxWidth: 1600, maxHeight: '90vh', margin: 'auto' } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e8f5fe' }}>
                    <DialogTitle style={{ color: '#1976D2', fontSize: '25px' }}>INBOX</DialogTitle>
                </Box>
                <DialogContent style={{ backgroundColor: '#f4f9fc', padding: '24px 24px 16px' }}>
                    <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: '400px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <h3 style={{ color: '#00A9E0', marginBottom: 18, marginTop: 4, marginLeft: 0, fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3 }}>MESSAGE</h3>
                            <div style={{ flex: 1, minHeight: 360, overflowY: 'auto', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                {signatureRequestDialog.content && (
                                    <>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            Hello! Please review the attached document and obtain the licensee&apos;s signature at your earliest convenience. Once signed, you can return it to our team by uploading it to this message in your Client Inbox.
                                        </Typography>
                                        <Box component="dl" sx={{ m: 0, '& dd': { m: 0, ml: 2 }, '& dt': { fontWeight: 600, mt: 1.5 } }}>
                                            <Typography component="dt">Licensee:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.licensee}</Typography>
                                            <Typography component="dt">State:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.state}</Typography>
                                            <Typography component="dt">Name of Form:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.nameOfForm}</Typography>
                                            <Typography component="dt">Due Date:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.dueDate}</Typography>
                                            <Typography component="dt">Signature Type:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.signatureType}</Typography>
                                            <Typography component="dt">Notarization Required:</Typography>
                                            <Typography component="dd">{signatureRequestDialog.content.notarizationRequired}</Typography>
                                        </Box>
                                    </>
                                )}
                            </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, minHeight: 28, marginLeft: 0 }}>
                                <h3 style={{ color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', margin: 0, lineHeight: 1.3 }}>ATTACHMENTS</h3>
                                <Button variant="contained" onClick={handleUploadSignatureRequestFromInfo} sx={{ backgroundColor: '#1976D2', color: '#fff', textTransform: 'none', fontWeight: 600 }}>
                                    Upload Document
                                </Button>
                            </div>
                            <div style={{ flex: 1, minHeight: 360, display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                <h3 style={{ color: '#000', fontWeight: 'bold', fontSize: '16px', marginBottom: 12, marginLeft: 0 }}>History</h3>
                                <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                                    {signatureRequestDialog.documents.length > 0 ? (
                                        <DataGridPro
                                            rows={signatureRequestDialog.documents}
                                            getRowId={(r) => String(r?.id ?? r?.documentUrl ?? '')}
                                            columns={[
                                                {
                                                    field: 'documentName',
                                                    headerName: 'File Name',
                                                    flex: 2,
                                                    minWidth: 180,
                                                    sortable: false,
                                                    renderCell: (params) => (
                                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                            {params.value || 'Document'}
                                                        </Typography>
                                                    ),
                                                },
                                                {
                                                    field: 'sentBy',
                                                    headerName: 'Uploaded By',
                                                    flex: 1,
                                                    minWidth: 120,
                                                    valueGetter: (params: any) => {
                                                        const miscRoles = ['Super Admin', 'MISC Filer', 'MISC State Reporter'];
                                                        const roles = (params.row.createdByUserRole ?? '').split(',').map((r: string) => r.trim());
                                                        const hasMiscRole = roles.some((r: string) => miscRoles.includes(r));
                                                        return hasMiscRole ? 'miscstaff' : (params.row.sentBy ?? 'N/A');
                                                    },
                                                },
                                                {
                                                    field: 'createdDate',
                                                    headerName: 'Upload Date',
                                                    flex: 1,
                                                    minWidth: 130,
                                                    renderCell: (params) => formatUtcToLocal(params.value, 'MM/DD/YYYY HH:mm'),
                                                },
                                                {
                                                    field: 'action',
                                                    headerName: 'Actions',
                                                    flex: 0.6,
                                                    minWidth: 90,
                                                    sortable: false,
                                                    renderCell: (params) => (
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ color: LightBlueBtnColor }}
                                                                    onClick={() => {
                                                                        if (params.row.documentUrl && params.row.documentName) {
                                                                            setDocumentsToView([{ documentUrl: params.row.documentUrl, documentName: params.row.documentName }]);
                                                                            setCurrentDocumentIndex(0);
                                                                            setDocumentViewerOpen(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    <FaRegEye />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Download" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ color: LightBlueBtnColor }}
                                                                    onClick={() => params.row.documentUrl && params.row.documentName && downloadSingleDocument(params.row.documentUrl, params.row.documentName)}
                                                                >
                                                                    <FaDownload />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ),
                                                },
                                            ]}
                                            hideFooter
                                            disableSelectionOnClick
                                            sx={{ border: 0 }}
                                            components={{ NoRowsOverlay: () => <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography> }}
                                        />
                                    ) : (
                                        <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button onClick={handleCloseSignatureRequestInfo} variant="outlined" sx={{ backgroundColor: '#E0E0E0', color: '#000', borderColor: '#ccc' }}>Cancel</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Tag Notification – Message Center layout (MESSAGE = filer notes, ATTACHMENTS = documents) */}
            <Dialog
                open={tagNotificationDialog.open}
                onClose={handleCloseTagNotificationInfo}
                fullWidth
                maxWidth="xl"
                PaperProps={{ style: { width: '98%', maxWidth: 1600, maxHeight: '90vh', margin: 'auto' } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e8f5fe' }}>
                    <DialogTitle style={{ color: '#1976D2', fontSize: '25px' }}>INBOX</DialogTitle>
                </Box>
                <DialogContent style={{ backgroundColor: '#f4f9fc', padding: '24px 24px 16px' }}>
                    <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: '400px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <h3 style={{ color: '#00A9E0', marginBottom: 7, marginLeft: 0, fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3 }}>MESSAGE</h3>
                            <div style={{ flex: 1, minHeight: 360, maxHeight: 500, overflowY: 'auto', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                {tagNotificationDialog.reportTag && (
                                    <>
                                        <DialogContentText sx={{ textAlign: 'left', mb: 0.5 }}><strong>Error Types</strong></DialogContentText>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                            {tagNotificationDialog.reportTag.errorTagCodeIds?.map((id: number) => (
                                                <Chip key={id} label={tagNotificationDialog.errorTagCodes.find((c: ErrorTagCodeModel) => c.errorTagCodeId === id)?.errorTagName} color="primary" variant="outlined" size="small" />
                                            ))}
                                        </Box>
                                        <DialogContentText sx={{ textAlign: 'left', my: 0.5, mb: 1 }}><strong>Number of Tags :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.isMultipleTags ? 'Multiple' : 'Single'}</Box></DialogContentText>
                                        <Box sx={{ textAlign: 'left', mb: 2 }}>
                                            <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Client :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.agency ?? ''}</Box></DialogContentText>
                                            {!tagNotificationDialog.reportTag.isMultipleTags && (
                                                <>
                                                    <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Invoice Number :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.invoiceNumber ?? ''}</Box></DialogContentText>
                                                    <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Policy Number :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.policyNumber ?? ''}</Box></DialogContentText>
                                                    <DialogContentText sx={{ textAlign: 'left', my: 0.5 }}><strong>Insured Name :</strong> <Box component="span" sx={{ color: 'text.primary' }}>{tagNotificationDialog.reportTag.insuredName ?? ''}</Box></DialogContentText>
                                                </>
                                            )}
                                        </Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Messages</Typography>
                                    </>
                                )}
                                {tagNotificationDialog.filerNotes.length > 0 ? (
                                    tagNotificationDialog.filerNotes.map((item, index) => {
                                        const miscRoles = ['Super Admin', 'MISC Filer', 'MISC State Reporter'];
                                        const roles = item.sentByUserRole?.split(',').map((r: string) => r.trim()) ?? [];
                                        const hasMiscRole = roles.some((r: string) => miscRoles.includes(r));
                                        const displaySentBy = hasMiscRole ? 'miscstaff' : (item.sentBy ?? '');
                                        return (
                                        <Box key={index} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {[displaySentBy, item.sentOn && formatUtcToLocal(item.sentOn, 'MM/DD/YYYY hh:mm A')].filter(Boolean).join(' • ')}
                                            </Typography>
                                            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>
                                                {item.filerNote}
                                            </Typography>
                                        </Box>
                                        );
                                    })
                                ) : (
                                    <Typography sx={{ color: '#666' }}>No messages available</Typography>
                                )}
                            </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, minHeight: 28, marginLeft: 0 }}>
                                <h3 style={{ color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', margin: 0, lineHeight: 1.3 }}>ATTACHMENTS</h3>
                                <Button variant="contained" onClick={handleUploadDocumentFromTagNotificationInfo} sx={{ backgroundColor: '#1976D2', color: '#fff', textTransform: 'none', fontWeight: 600 }}>
                                    Upload Document
                                </Button>
                            </div>
                            <div style={{ flex: 1, minHeight: 360, display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                <h3 style={{ color: '#000', fontWeight: 'bold', fontSize: '16px', marginBottom: 12, marginLeft: 0 }}>History</h3>
                                <div style={{ width: '100%' }}>
                                    {tagNotificationDialog.documents.length > 0 ? (
                                        <DataGridPro
                                            rows={tagNotificationDialog.documents}
                                            getRowId={(r) => String(r?.id ?? r?.documentUrl ?? '')}
                                            autoHeight
                                            columns={[
                                                {
                                                    field: 'documentName',
                                                    headerName: 'File Name',
                                                    flex: 2,
                                                    minWidth: 180,
                                                    sortable: false,
                                                    renderCell: (params) => (
                                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                                            {params.value || 'Document'}
                                                        </Typography>
                                                    ),
                                                },
                                                {
                                                    field: 'sentBy',
                                                    headerName: 'Uploaded By',
                                                    flex: 1,
                                                    minWidth: 120,
                                                    valueGetter: (params) => {
                                                        const miscRoles = ['Super Admin', 'MISC Filer', 'MISC State Reporter'];
                                                        const roles = (params.row.createdByUserRole ?? '').split(',').map((r: string) => r.trim());
                                                        const hasMiscRole = roles.some((r: string) => miscRoles.includes(r));
                                                        return hasMiscRole ? 'miscstaff' : (params.row.sentBy ?? 'N/A');
                                                    },
                                                },
                                                {
                                                    field: 'createdDate',
                                                    headerName: 'Upload Date',
                                                    flex: 1,
                                                    minWidth: 130,
                                                    renderCell: (params) => formatUtcToLocal(params.value, 'MM/DD/YYYY HH:mm'),
                                                },
                                                {
                                                    field: 'action',
                                                    headerName: 'Actions',
                                                    flex: 0.6,
                                                    minWidth: 90,
                                                    sortable: false,
                                                    renderCell: (params) => (
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ color: LightBlueBtnColor }}
                                                                    onClick={() => {
                                                                        if (params.row.documentUrl && params.row.documentName) {
                                                                            setDocumentsToView([{ documentUrl: params.row.documentUrl, documentName: params.row.documentName }]);
                                                                            setCurrentDocumentIndex(0);
                                                                            setDocumentViewerOpen(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    <FaRegEye />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Download" arrow>
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ color: LightBlueBtnColor }}
                                                                    onClick={() => params.row.documentUrl && params.row.documentName && downloadSingleDocument(params.row.documentUrl, params.row.documentName)}
                                                                >
                                                                    <FaDownload />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ),
                                                },
                                            ]}
                                            pagination
                                            pageSize={5}
                                            rowsPerPageOptions={[5]}
                                            disableSelectionOnClick
                                            sx={{ border: 0 }}
                                            components={{ NoRowsOverlay: () => <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography> }}
                                        />
                                    ) : (
                                        <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <Box sx={{ mt:2}}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Reply to MISC</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Enter your reply..."
                            value={tagNotificationReplyToMisc}
                            onChange={(e) => setTagNotificationReplyToMisc(e.target.value)}
                            size="small"
                            sx={{ mb: 1.5 }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={handleCloseTagNotificationInfo} variant="outlined" sx={{ backgroundColor: '#E0E0E0', color: '#000', borderColor: '#ccc' }}>Cancel</Button>
                            <Button onClick={handleReplyToMiscFromTag} variant="contained" disabled={!tagNotificationReplyToMisc?.trim()} sx={{ backgroundColor: '#1976D2', color: '#fff', textTransform: 'none', fontWeight: 600 }}>Reply to MISC</Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Single Attached Documents dialog (Tag Notification, Signature Request, etc.) */}
            <Dialog open={attachedDocumentsOpen} onClose={handleCloseAttachedDocuments} maxWidth="lg" fullWidth>
                <DialogTitle className="card-title">Attached Documents</DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div style={{ height: '50vh', width: '100%' }}>
                            <DataGridPro
                                rows={attachedDocumentsList}
                                getRowId={(r) => r?.id ?? r?.documentUrl ?? ''}
                                columns={[
                                    { field: 'documentName', headerName: 'Document Name', flex: 3, minWidth: 200 },
                                    { field: 'sentBy', headerName: 'Sent By', flex: 1.5, minWidth: 120 },
                                    {
                                        field: 'createdDate',
                                        headerName: 'Date Uploaded',
                                        flex: 1,
                                        minWidth: 120,
                                        renderCell: (params) => formatUtcToLocal(params.value, 'MM/DD/YYYY'),
                                    },
                                    {
                                        field: 'action',
                                        headerName: 'Actions',
                                        flex: 1,
                                        minWidth: 120,
                                        align: 'center',
                                        headerAlign: 'center',
                                        renderCell: (params) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                                <Tooltip title="View Document" arrow>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: LightBlueBtnColor }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (params.row.documentUrl && params.row.documentName) {
                                                                viewDocumentFromList(params.row.documentUrl, params.row.documentName);
                                                            }
                                                        }}
                                                    >
                                                        <FaRegEye />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Download Document" arrow>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: LightBlueBtnColor }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (params.row.documentUrl && params.row.documentName) {
                                                                downloadSingleDocument(params.row.documentUrl, params.row.documentName);
                                                            }
                                                        }}
                                                    >
                                                        <FaDownload />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        ),
                                    },
                                ]}
                                sx={{ border: 0 }}
                                hideFooter
                                disableSelectionOnClick
                            />
                        </div>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <button className="btn-2 btn d-flex btn-primary search-btn" type="button" onClick={handleCloseAttachedDocuments}>
                        Close
                    </button>
                </DialogActions>
            </Dialog>

            {/* Stamped Document – Message Center layout (no Reply, no Send & Return, no Upload) */}
            <Dialog
                open={stampedDocumentInfoOpen}
                onClose={handleCloseStampedDocumentInfo}
                fullWidth
                maxWidth="xl"
                PaperProps={{ style: { width: '98%', maxWidth: 1600, maxHeight: '90vh', margin: 'auto' } }}
            >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e8f5fe' }}>
                    <DialogTitle style={{ color: '#1976D2', fontSize: '25px' }}>INBOX</DialogTitle>
                </Box>
                <DialogContent style={{ backgroundColor: '#f4f9fc', padding: '24px 24px 16px' }}>
                    <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: '400px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <h3 style={{ color: '#00A9E0', marginBottom: 12, marginTop: 2, marginLeft: 0, fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3 }}>MESSAGE</h3>
                            <div style={{ flex: 1, minHeight: 360, overflowY: 'auto', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                {stampedDocumentInfoContent && (
                                    <>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {stampedDocumentInfoContent.message}
                                        </Typography>
                                        <Link href={stampedDocumentInfoContent.linkUrl} target="_blank" rel="noopener noreferrer" sx={{ color: PrimaryBlueColor, wordBreak: 'break-all' }}>
                                            {stampedDocumentInfoContent.linkUrl}
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ marginBottom: 12, minHeight: 28, marginLeft: 0 }}>
                                <h3 style={{ color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', margin: 0, lineHeight: 1.3 }}>ATTACHMENTS</h3>
                            </div>
                            <div style={{ flex: 1, minHeight: 360, display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                <h3 style={{ color: '#000', fontWeight: 'bold', fontSize: '16px', marginBottom: 12, marginLeft: 0 }}>History</h3>
                                <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                                {pendingStampedDocuments.length > 0 ? (
                                    <DataGridPro
                                        rows={pendingStampedDocuments}
                                        getRowId={(row: { documentUrl: string; documentName: string }) => row.documentUrl || row.documentName || `doc-${row.documentName}`}
                                        columns={[
                                            { field: 'documentName', headerName: 'Document Name', flex: 2, minWidth: 180 },
                                            { field: 'action', headerName: 'Actions', flex: 0.6, minWidth: 100, sortable: false, renderCell: (params) => (
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="View" arrow><IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => { setDocumentsToView([{ documentUrl: params.row.documentUrl, documentName: params.row.documentName }]); setCurrentDocumentIndex(0); setDocumentViewerOpen(true); }}><FaRegEye /></IconButton></Tooltip>
                                                    <Tooltip title="Download" arrow><IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => downloadSingleDocument(params.row.documentUrl, params.row.documentName)}><FaDownload /></IconButton></Tooltip>
                                                </Box>
                                            ) }
                                        ]}
                                        hideFooter
                                        disableSelectionOnClick
                                        sx={{ border: 0 }}
                                    />
                                ) : (
                                    <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography>
                                )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                        <Button onClick={handleCloseStampedDocumentInfo} variant="outlined" sx={{ backgroundColor: '#E0E0E0', color: '#000', borderColor: '#ccc' }}>Cancel</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* document viewer */}
            <DocumentViewer open={documentViewerOpen}
                documents={documentsToView}
                onClose={handleCloseDocumentViewer}
            />

            {/* upload report tag documents */}
            <UploadReportTagDocuments uploadDocumentsOpen={uploadReportTagDocumentsOpen} 
                handleCloseUploadDocuments={handleCloseUploadReportTagDocuments} 
                reportTag={reportTag as ReportTagModel}
                clientDocumentData={clientDocumentData} 
                actionFunction={() => {
                    fetchCustomerInbox();
                }} 
            />

            {/* upload signature request documents */}
            <UploadSignatureRequestDocuments
                open={uploadSignatureRequestOpen}
                onClose={handleCloseUploadSignatureRequest}
                signatureRequestId={selectedSignatureRequestIdForUpload}
                onSuccess={fetchCustomerInbox}
            />
        </Box>
        </LocalizationProvider>
    );
};